"use client";

import React from "react";
import { Check, X } from "lucide-react";
import type { PlatformType } from "./utils-v2";
import {
  getSlipBreakevens,
  adjustBreakevensForOdds,
  getPlatformDisplayName,
  type SlipTypeBreakeven,
} from "./props-slip-breakevens";
import { getUnderdogMultiplier } from "./utils-v2";
import { cn } from "@/lib/utils";
import type { Grading } from "@/lib/fantasy-props/rows";

export interface OddsToHitTooltipProps {
  platform: PlatformType;
  oddsToHitPercent?: number | null;
  opposingBook?: string | null;
  americanOdds?: number | null;
  grading: Grading;
  variant?: "header" | "cell";
}

export function OddsToHitTooltipContent({
  platform,
  oddsToHitPercent,
  opposingBook,
  americanOdds,
  grading,
  variant = "cell",
}: OddsToHitTooltipProps): React.ReactElement {
  const standard = grading.kind === "graded" ? grading.standard : null;
  const baseBreakevens = grading.kind === "ungraded" ? null : getSlipBreakevens(platform);
  const adjustedBreakevens = baseBreakevens
    ? adjustBreakevensForOdds(platform, baseBreakevens, americanOdds, standard)
    : null;
  const breakevens = adjustedBreakevens ?? baseBreakevens;
  const platformName = getPlatformDisplayName(platform);
  const isAdjusted = adjustedBreakevens !== null;
  const multiplier = platform === "underdog" ? getUnderdogMultiplier(americanOdds ?? null, standard ?? undefined) : null;

  if (!breakevens || breakevens.length === 0) {
    return (
      <div className="text-xs text-muted-foreground/70">
        % Needed data not available for {platformName}
      </div>
    );
  }

  const sortedBreakevens = [...breakevens].sort(
    (a, b) => a.breakevenPct - b.breakevenPct
  );

  return (
    <div className="min-w-[200px] max-w-[280px]">
      <div className="flex items-center gap-2 pb-2 mb-2 border-b border-white/10">
        <div className="flex flex-col">
          <span className="text-xs font-semibold text-foreground/90">
            {platformName} % Needed
          </span>
          {isAdjusted && multiplier !== null && (
            <span className="text-[10px] font-medium text-accent-value/80 data-num">
              Adjusted for {multiplier.toFixed(2)}x payout
            </span>
          )}
        </div>
        {variant === "cell" && oddsToHitPercent !== null && oddsToHitPercent !== undefined && (
          <span className="ml-auto text-xs data-num text-muted-foreground">
            {oddsToHitPercent.toFixed(1)}%
          </span>
        )}
      </div>

      <div className="space-y-1">
        {sortedBreakevens.map((slip) => (
          <SlipTypeRow
            key={slip.id}
            slip={slip}
            oddsToHitPercent={oddsToHitPercent}
            showQualification={variant === "cell"}
          />
        ))}
      </div>

      {variant === "cell" && opposingBook && (
        <div className="mt-2 pt-2 border-t border-white/10 text-[10px] font-medium text-muted-foreground/70">
          <span>vs {opposingBook}</span>
        </div>
      )}
    </div>
  );
}

interface SlipTypeRowProps {
  slip: SlipTypeBreakeven;
  oddsToHitPercent?: number | null;
  showQualification: boolean;
}

function SlipTypeRow({ slip, oddsToHitPercent, showQualification }: SlipTypeRowProps) {
  const qualifies =
    showQualification &&
    oddsToHitPercent !== null &&
    oddsToHitPercent !== undefined &&
    oddsToHitPercent >= slip.breakevenPct;

  return (
    <div
      className={cn(
        "flex items-center justify-between gap-3 py-0.5 text-xs",
        qualifies && "text-accent-value"
      )}
    >
      <div className="flex items-center gap-1.5">
        {showQualification && oddsToHitPercent !== null && oddsToHitPercent !== undefined && (
          <span className="w-3.5 flex-shrink-0">
            {qualifies ? (
              <Check className="w-3 h-3 text-accent-value" />
            ) : (
              <X className="size-3 text-muted-foreground/70" />
            )}
          </span>
        )}
        <span className={cn(
          "text-foreground/80",
          qualifies && "text-accent-value font-medium"
        )}>
          {slip.label}
        </span>
      </div>
      <span
        className={cn(
          "data-num tabular-nums",
          qualifies ? "text-accent-value" : "text-muted-foreground/70"
        )}
      >
        {slip.breakevenPct}%
      </span>
    </div>
  );
}
